import { Directive, inject, NgZone, DestroyRef, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocomplete } from '@angular/material/autocomplete';

import { fromEvent } from 'rxjs';
import { WINDOW } from 'app/providers/window.provider';

@Directive({
  selector: '[autoCompletePanelZoom]',
})
export class AutocompletePanelZoomDirective {
  private panelObserver: MutationObserver;
  private ngZone = inject(NgZone);
  private renderer2 = inject(Renderer2);
  private window: Window = inject(WINDOW);
  private readonly destroyRef = inject(DestroyRef);

  private readonly zoomLevelMargins = [
    { threshold: 1.25, margins: { top: '6vh', left: '3.3vw' } },
    { threshold: 1.5, margins: { top: '6vh', left: '2.8vw' } },
    { threshold: 1.75, margins: { top: '6vh', left: '4.7vw' } },
  ].sort((a, b) => b.threshold - a.threshold);

  constructor(private matAutocomplete: MatAutocomplete) {
    this.panelObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const panel = this.matAutocomplete.panel?.nativeElement;
          if (panel) {
            // Run the style updates inside NgZone to ensure change detection
            this.ngZone.run(() => {
              this.adjustMarginBasedOnZoom();
            });
          }
        }
      });
    });
  }

  ngAfterViewInit(): void {
    // Initial adjustment when the autocomplete opens
    this.matAutocomplete.opened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // First try to adjust immediately if panel is already available
      const panel = this.matAutocomplete.panel?.nativeElement;
      if (panel) {
        this.ngZone.run(() => {
          this.adjustMarginBasedOnZoom();
        });
      }

      // Then start observing for subsequent changes
      this.panelObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });

    // Listen for window resize events
    fromEvent(this.window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.matAutocomplete.panel?.nativeElement) {
          this.adjustMarginBasedOnZoom();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.panelObserver) {
      this.panelObserver.disconnect();
    }
  }

  private adjustMarginBasedOnZoom(): void {
    const zoomLevel: number = this.window.devicePixelRatio;
    const panel = this.matAutocomplete.panel?.nativeElement;

    if (!panel) {
      return;
    }

    const config = this.zoomLevelMargins.find(({ threshold }) => zoomLevel >= threshold);

    if (config) {
      this.renderer2.setStyle(panel, 'margin-top', config.margins.top);
      this.renderer2.setStyle(panel, 'margin-left', config.margins.left);
    }
  }
}
